import { Assembly, PointOfInterest } from '@/__generated__/graphql';
import { Stack } from '@mui/material';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { areEqual, FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { SummaryCard } from '../../PointsOfInterest/SummaryCard';
import { useSetAtom } from 'jotai';
import * as analysisState from '@/components/Analysis/state';
import * as localState from '@/components/Analysis/modules/pointOfInterest/state';
import styled from 'styled-components';

const PAGE_SIZE = 10;

// Create a styled wrapper for the FixedSizeList component
const StyledList = styled(List)`
  ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PointsOfInterestList = ({
  pointsOfInterest,
  type,
}: {
  pointsOfInterest: PointOfInterest[];
  type: string;
}) => {
  const setSelectedAssemblyId = useSetAtom(analysisState.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(analysisState.selectedAssemblyName);
  const setSelectedPointOfInterestId = useSetAtom(localState.selectedPointOfInterestId);
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemSize, setItemSize] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (currentContainer?.offsetHeight) setContainerHeight(currentContainer.offsetHeight);
      if (currentContainer?.offsetWidth) setContainerWidth(currentContainer.offsetWidth);
      const rootFontSize = getComputedStyle(document.documentElement);
      const parsed = parseFloat(rootFontSize.fontSize);
      setItemSize(7 * parsed);
    });

    if (currentContainer) resizeObserver.observe(currentContainer);

    return () => {
      if (currentContainer) resizeObserver.unobserve(currentContainer);
    };
  }, [containerRef]);

  const listHeight = useMemo(() => {
    const actualHeight = itemSize * pointsOfInterest.length;
    const maxHeight = itemSize * PAGE_SIZE - itemSize * 0.6;
    return Math.min(actualHeight, maxHeight);
  }, [itemSize, pointsOfInterest.length]);

  const handleClick = (poiId: string | undefined, assembly: Assembly | undefined) => {
    setSelectedPointOfInterestId(poiId);
    setSelectedAssemblyId(assembly?.id);
    setSelectedAssemblyName(assembly?.tagName);
  };

  const Row = memo(
    ({ index, style }: ListChildComponentProps) => (
      <div style={style}>
        <SummaryCard
          data={pointsOfInterest[index]}
          poiIndex={index}
          variant="compact"
          onClick={handleClick}
          poiType={type}
        />
      </div>
    ),
    areEqual
  );

  return (
    <Stack gap="1.2rem">
      <Stack ref={containerRef}>
        <StyledList
          key={`${containerHeight}-${containerWidth}-${pointsOfInterest.length}`}
          height={listHeight}
          itemCount={pointsOfInterest.length}
          width="100%"
          itemSize={itemSize}
        >
          {Row}
        </StyledList>
      </Stack>
    </Stack>
  );
};
